/**
 * Who is signed in, and getting them in.
 *
 * The two pages share this so they cannot disagree about what a session is.
 * `index.html` is the form and creates one; `app.html` refuses to draw
 * anything without one. That is the whole of the front door.
 *
 * Nothing about the session is kept in localStorage: a role there would be a
 * role the person holding the browser can edit. The role is a custom claim on the
 * Firebase token, the token is signed, and the Firestore rules read the same
 * claim. Editing it here would change what this page draws and nothing about
 * what the server will hand over.
 */

import {
  type ConfirmationResult,
  RecaptchaVerifier,
  getIdTokenResult,
  linkWithPhoneNumber,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  updatePassword,
} from "firebase/auth";
import type { User } from "firebase/auth";
import { signOut as endSession } from "firebase/auth";
import { loginEmail } from "@liyu/core";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { auth, config, db, functions } from "./firebase";

export type AppRole = "resident" | "accountant" | "committee" | "platform_admin";
export type Portal = "resident" | "staff";

export interface Session {
  user: User;
  role: AppRole;
  orgId: string;
  /** Absent on a staff account, which belongs to the org rather than a door. */
  buildingId: string | undefined;
  unitId: string | undefined;
  termId: string | undefined;
}

/**
 * Signed in, at the wrong door.
 *
 * A signpost, not a lock. The lock is in the Firestore rules, where a client
 * cannot argue with it. This exists so that somebody who types a staff id into
 * the residents' form is told so, rather than landing in a screen built for
 * somebody else with no way to tell why it looks wrong.
 */
export class WrongPortalError extends Error {
  constructor(readonly portal: Portal) {
    super(`this account cannot sign in at the ${portal} door`);
    this.name = "WrongPortalError";
  }
}

function allowedAt(portal: Portal, role: AppRole | undefined): boolean {
  if (role === undefined) {
    return false;
  }
  return portal === "resident"
    ? role === "resident"
    : role === "accountant" || role === "committee";
}

/**
 * A resident signed in with the one-time code from their slip, who confirms a
 * phone and chooses a password next. `vouched` is the office having confirmed
 * them in person, which skips the text message.
 */
export class ResidentSetupError extends Error {
  constructor(
    readonly vouched: boolean,
    readonly confirmedPhone: string | null,
  ) {
    super("this resident has to confirm a phone and choose a password first");
    this.name = "ResidentSetupError";
  }
}

/** Staff signed in with a one-time code, who must choose a password next. */
export class SetupRequiredError extends Error {
  constructor() {
    super("this account has to choose a password before it can go further");
    this.name = "SetupRequiredError";
  }
}

async function readSession(user: User): Promise<Session | null> {
  const token = await getIdTokenResult(user);
  const role = token.claims.appRole as AppRole | undefined;
  const orgId = token.claims.orgId as string | undefined;
  // The rules refuse an account in setup everything, so there is no app to open.
  if (role === undefined || orgId === undefined || token.claims.setup === true) {
    return null;
  }
  return {
    user,
    role,
    orgId,
    buildingId: token.claims.buildingId as string | undefined,
    unitId: token.claims.unitId as string | undefined,
    termId: token.claims.termId as string | undefined,
  };
}

export async function signIn(
  orgCode: string,
  identity: string,
  password: string,
  portal: Portal,
): Promise<void> {
  const credential = await signInWithEmailAndPassword(
    auth,
    loginEmail(orgCode, identity, config.loginDomain),
    password,
  );
  const token = await getIdTokenResult(credential.user);
  if (token.claims.setup === true) {
    if (token.claims.appRole === "resident") {
      if (portal !== "resident") {
        await endSession(auth);
        throw new WrongPortalError(portal);
      }
      // An account in setup may read its own profile and nothing else.
      const profile = await getDoc(doc(db, "users", credential.user.uid));
      throw new ResidentSetupError(
        profile.get("verifiedInPerson") === true,
        credential.user.phoneNumber,
      );
    }
    if (allowedAt(portal, token.claims.appRole as AppRole | undefined)) {
      throw new SetupRequiredError();
    }
  }
  const session = await readSession(credential.user);
  if (!allowedAt(portal, session?.role)) {
    // Straight back out. Leaving the session open and only hiding the screens
    // would put somebody in the app as a role the rest of it does not expect,
    // which is a worse state than being signed out.
    await endSession(auth);
    throw new WrongPortalError(portal);
  }
}

/**
 * Replaces the one-time code with the staff member's own password. The function
 * lifts the setup claim, and the refreshed token is what lets the app read.
 */
export async function finishStaffSetup(newPassword: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Sign in again to finish setting up");
  }
  await updatePassword(user, newPassword);
  await httpsCallable(functions, "completeFirstLoginCall")({});
  await user.getIdToken(true);
}

let setupVerifier: RecaptchaVerifier | null = null;
let setupConfirmation: ConfirmationResult | null = null;

/**
 * Texting a code to the phone a resident is confirming at first login. The code
 * links the number to this account, which is what the function reads; a typed
 * number is never believed.
 */
export async function sendSetupCode(phone: string, captchaContainer: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Sign in again to finish setting up");
  }
  setupVerifier ??= new RecaptchaVerifier(auth, captchaContainer, { size: "invisible" });
  try {
    setupConfirmation = await linkWithPhoneNumber(user, phone, setupVerifier);
  } catch (cause) {
    setupVerifier.clear();
    setupVerifier = null;
    throw cause;
  }
}

export async function confirmSetupCode(code: string): Promise<void> {
  if (!setupConfirmation) {
    throw new Error("No code was requested");
  }
  await setupConfirmation.confirm(code.trim());
  setupConfirmation = null;
}

/**
 * The resident's own password, their name and number, and the setup claim
 * lifted. The function checks the phone on the account, not the one typed here.
 */
export async function finishResidentSetup(details: {
  name: string;
  phone: string;
  password: string;
}): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Sign in again to finish setting up");
  }
  await updatePassword(user, details.password);
  await httpsCallable(
    functions,
    "completeFirstLoginCall",
  )({
    name: details.name,
    phone: details.phone,
  });
  await user.getIdToken(true);
}

/** A phone number that belongs to no account here. */
export class NoAccountError extends Error {
  constructor() {
    super("no account uses that phone number");
    this.name = "NoAccountError";
  }
}

let verifier: RecaptchaVerifier | null = null;
let confirmation: ConfirmationResult | null = null;

/**
 * Texting a reset code to the phone a resident confirmed at first login, the
 * same flow the phone app uses. The reCAPTCHA is invisible; a refused one is
 * spent, so it is thrown away and made again for the next try.
 */
export async function sendResetCode(phone: string, captchaContainer: string): Promise<void> {
  verifier ??= new RecaptchaVerifier(auth, captchaContainer, { size: "invisible" });
  try {
    confirmation = await signInWithPhoneNumber(auth, phone, verifier);
  } catch (cause) {
    verifier.clear();
    verifier = null;
    throw cause;
  }
}

/**
 * Checking the code signs in as whoever owns the number. A number nobody owns
 * makes Firebase create a new, empty account; that one is deleted at once and
 * the person is told, rather than left signed in as nobody.
 */
export async function confirmResetCode(code: string): Promise<void> {
  if (!confirmation) {
    throw new Error("No code was requested");
  }
  const { user } = await confirmation.confirm(code.trim());
  confirmation = null;
  if ((await getIdTokenResult(user)).claims.appRole === undefined) {
    await user.delete();
    throw new NoAccountError();
  }
}

export async function applyNewPassword(password: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("The code was not confirmed");
  }
  await updatePassword(user, password);
}

export async function signOut(): Promise<void> {
  await endSession(auth);
}

/**
 * Calls back once if this browser's login goes away while a page is open: a
 * sign-out in another tab, or the account switched off. Firebase answers
 * straight away with whoever is signed in now, so this is for a page that
 * already has a session.
 */
export function onSignedOut(then: () => void): void {
  const stop = onAuthStateChanged(auth, (user) => {
    if (user === null) {
      stop();
      then();
    }
  });
}

/**
 * The session, once Firebase has finished deciding whether there is one.
 *
 * Resolving with null rather than throwing, because "nobody is signed in" is
 * the expected answer on the sign-in page and an error on the app page, and
 * only the caller knows which it is looking at.
 */
export function currentSession(): Promise<Session | null> {
  return new Promise((resolve, reject) => {
    const stop = onAuthStateChanged(
      auth,
      (user) => {
        stop();
        if (!user) {
          resolve(null);
          return;
        }
        readSession(user).then(resolve, reject);
      },
      (cause: Error) => {
        stop();
        reject(cause);
      },
    );
  });
}
